import { useQuery } from "@tanstack/react-query";
import { customAxios } from "lib/axios";

export interface AuditLogActor {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
}

export interface AuditLogEntry {
  _id: string;
  action: string;
  module: string;
  targetModel?: string;
  targetId?: string;
  performedBy?: AuditLogActor | string | null;
  oldValue?: unknown;
  newValue?: unknown;
  changedFields?: string[];
  ip?: string;
  userAgent?: string;
  data?: unknown;
  createdAt: string;
}

export interface AuditLogsResponse {
  success: boolean;
  data: {
    logs: AuditLogEntry[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      limit: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

export interface AuditLogFilters {
  email?: string;
  targetModel?: string;
  action?: string;
  module?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  limit?: number;
}

const fetchAuditLogs = async (filters: AuditLogFilters) => {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== undefined && v !== "")
  );
  const { data } = await customAxios.get<AuditLogsResponse>("/logs", { params });
  return data.data;
};

export const useAuditLogs = (filters: AuditLogFilters) =>
  useQuery({
    queryKey: ["audit-logs", filters],
    queryFn: () => fetchAuditLogs(filters),
    keepPreviousData: true,
  });

export interface AuditFilterOptions {
  success: boolean;
  data: {
    actions: string[];
    modules: string[];
    targetModels: string[];
  };
}

const fetchAuditFilters = async () => {
  const { data } = await customAxios.get<AuditFilterOptions>("/logs/filters");
  return data.data;
};

export const useAuditFilters = () =>
  useQuery({
    queryKey: ["audit-log-filters"],
    queryFn: fetchAuditFilters,
  });
